import { Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, GraduationCap, Users, LogOut, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../i18n";
import Dashboard from "./Dashboard";
import ProductsAdmin from "./ProductsAdmin";
import ProductForm from "./ProductForm";
import OrdersAdmin from "./OrdersAdmin";
import CoursesAdmin from "./CoursesAdmin";
import CourseForm from "./CourseForm";
import CustomersAdmin from "./CustomersAdmin";

export default function Admin() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  if (user === undefined) return <div className="min-h-screen bg-obsidian" />;
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-obsidian text-slate-100 flex flex-col items-center justify-center gap-6 px-6" data-testid="admin-login-required">
        <p className="font-serif text-2xl text-center max-w-md">{t("admin.login.required")}</p>
        <button onClick={() => navigate("/accedi?redirect=/admin")} data-testid="admin-goto-login"
          className="bg-gold text-obsidian px-7 py-3.5 font-semibold hover:bg-honey transition-colors">
          {t("nav.login")}
        </button>
      </div>
    );
  }

  const tabs = [
    { to: "/admin", end: true, label: t("admin.dashboard"), icon: LayoutDashboard, testid: "admin-tab-dashboard" },
    { to: "/admin/prodotti", label: t("admin.products"), icon: Package, testid: "admin-tab-products" },
    { to: "/admin/ordini", label: t("admin.orders"), icon: ShoppingCart, testid: "admin-tab-orders" },
    { to: "/admin/corsi", label: t("admin.courses"), icon: GraduationCap, testid: "admin-tab-courses" },
    { to: "/admin/clienti", label: t("admin.customers"), icon: Users, testid: "admin-tab-customers" },
  ];

  return (
    <div className="min-h-screen bg-obsidian text-slate-100 flex flex-col lg:flex-row" data-testid="admin-panel">
      <aside className="lg:w-64 lg:min-h-screen bg-slate2/60 border-b lg:border-b-0 lg:border-r border-slate-700 flex lg:flex-col">
        <div className="px-6 py-6 border-b border-slate-700 hidden lg:block">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="LoMIELlina" className="h-10 w-10 rounded-full" />
            <span className="font-serif text-xl font-bold">Lo<span className="text-gold">MIEL</span>lina</span>
          </Link>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("admin.title")}</p>
        </div>
        <nav className="flex lg:flex-col flex-1 overflow-x-auto">
          {tabs.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-4 text-sm font-medium whitespace-nowrap border-l-2 transition-colors ${isActive ? "border-gold text-gold bg-gold/5" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block border-t border-slate-700 px-6 py-4 space-y-2">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100 transition-colors" data-testid="admin-back-store">
            <ArrowLeft size={14} /> Storefront
          </Link>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors">
            <LogOut size={14} /> {t("nav.logout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 px-6 lg:px-10 py-8 overflow-x-hidden">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="prodotti" element={<ProductsAdmin />} />
          <Route path="prodotti/nuovo" element={<ProductForm />} />
          <Route path="prodotti/modifica/:id" element={<ProductForm />} />
          <Route path="ordini" element={<OrdersAdmin />} />
          <Route path="corsi" element={<CoursesAdmin />} />
          <Route path="corsi/nuovo" element={<CourseForm />} />
          <Route path="corsi/modifica/:id" element={<CourseForm />} />
          <Route path="clienti" element={<CustomersAdmin />} />
        </Routes>
      </main>
    </div>
  );
}
